import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { api, User } from '../services/api'

export default function CreateGroupPage() {
  const navigate = useNavigate()
  const [groupName, setGroupName] = useState('')
  const [groupDescription, setGroupDescription] = useState('')
  const [searchQuery, setSearchQuery] = useState('')
  const [searchResults, setSearchResults] = useState<User[]>([])
  const [selectedMembers, setSelectedMembers] = useState<User[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [isSearching, setIsSearching] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Search users
  useEffect(() => {
    if (!searchQuery.trim()) {
      setSearchResults([])
      return
    }

    const timer = setTimeout(async () => {
      setIsSearching(true)
      try {
        const users = await api.searchUsers(searchQuery.trim())
        // Filter out already selected members
        const selectedIds = new Set(selectedMembers.map(m => m.id))
        setSearchResults(users.filter(u => !selectedIds.has(u.id)))
      } catch {
        console.error('Search failed')
      } finally {
        setIsSearching(false)
      }
    }, 300)

    return () => clearTimeout(timer)
  }, [searchQuery, selectedMembers])

  const addMember = (user: User) => {
    if (selectedMembers.length >= 499) {
      setError('群組最多 500 人')
      return
    }
    setSelectedMembers(prev => [...prev, user])
    setSearchQuery('')
    setSearchResults([])
  }

  const removeMember = (userId: string) => {
    setSelectedMembers(prev => prev.filter(m => m.id !== userId))
  }

  const handleCreate = async () => {
    if (!groupName.trim()) {
      setError('請輸入群組名稱')
      return
    }

    setIsLoading(true)
    setError(null)

    try {
      const group = await api.createGroup(
        groupName.trim(),
        selectedMembers.map(m => m.id)
      )
      navigate(`/group/${group.id}`)
    } catch (err) {
      setError('創建群組失敗，請重試')
      console.error('Create group failed:', err)
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="min-h-screen flex flex-col bg-white dark:bg-dark-bg">
      {/* Header */}
      <header className="app-bar">
        <button
          onClick={() => navigate(-1)}
          className="p-2 hover:bg-white/10 rounded-full transition-colors"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>
        <h1 className="text-lg font-semibold flex-1">建立群組</h1>
        <button
          onClick={handleCreate}
          disabled={isLoading || !groupName.trim()}
          className="px-4 py-1.5 bg-white/20 hover:bg-white/30 rounded-full text-sm font-medium disabled:opacity-50 transition-colors"
        >
          {isLoading ? '建立中...' : '建立'}
        </button>
      </header>

      <main className="flex-1 overflow-y-auto p-4">
        {/* Error Message */}
        {error && (
          <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 rounded-xl text-sm">
            {error}
          </div>
        )}

        {/* Group Avatar Placeholder */}
        <div className="flex justify-center mb-6">
          <div className="w-20 h-20 rounded-full bg-primary/20 flex items-center justify-center">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
          </div>
        </div>

        {/* Group Name Input */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            群組名稱
          </label>
          <input
            type="text"
            value={groupName}
            onChange={(e) => setGroupName(e.target.value)}
            placeholder="輸入群組名稱"
            className="input-field"
            maxLength={100}
          />
        </div>

        {/* Group Description */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            群組描述 <span className="text-gray-400">（可選）</span>
          </label>
          <textarea
            value={groupDescription}
            onChange={(e) => setGroupDescription(e.target.value)}
            placeholder="輸入群組描述..."
            className="input-field resize-none"
            maxLength={500}
            rows={3}
          />
        </div>

        {/* Selected Members */}
        {selectedMembers.length > 0 && (
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              已選擇成員 ({selectedMembers.length})
            </label>
            <div className="flex flex-wrap gap-2">
              {selectedMembers.map(member => (
                <div
                  key={member.id}
                  className="flex items-center gap-2 px-3 py-1.5 bg-primary/10 rounded-full"
                >
                  <div className="w-6 h-6 bg-primary text-white rounded-full flex items-center justify-center text-xs font-medium">
                    {member.username.charAt(0).toUpperCase()}
                  </div>
                  <span className="text-sm text-gray-800 dark:text-gray-200">
                    {member.username}
                  </span>
                  <button
                    onClick={() => removeMember(member.id)}
                    className="p-0.5 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-full"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Add Members Search */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            添加成員
          </label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="搜尋用戶..."
              className="input-field pl-12"
            />
            {isSearching && (
              <div className="absolute inset-y-0 right-0 pr-4 flex items-center">
                <div className="spinner w-4 h-4 border-primary border-t-primary-light" />
              </div>
            )}
          </div>

          {/* Search Results */}
          {searchResults.length > 0 && (
            <div className="mt-2 border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden">
              {searchResults.map(user => (
                <button
                  key={user.id}
                  onClick={() => addMember(user)}
                  className="w-full px-4 py-3 flex items-center gap-3 hover:bg-gray-50 dark:hover:bg-dark-surface transition-colors border-b border-gray-100 dark:border-gray-800 last:border-b-0"
                >
                  <div className="avatar avatar-sm">
                    {user.avatar ? (
                      <img src={user.avatar} alt={user.username} className="w-full h-full object-cover rounded-full" />
                    ) : (
                      user.username.charAt(0).toUpperCase()
                    )}
                  </div>
                  <div className="flex-1 text-left">
                    <p className="font-medium text-gray-900 dark:text-white">{user.username}</p>
                  </div>
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                </button>
              ))}
            </div>
          )}
        </div>
      </main>
    </div>
  )
}
